import gql from 'graphql-tag';

import { Subscription } from 'core/subscription';
import { Mutation, Query } from 'core/index';

export const FILE_FIELDS = gql`
  fragment fileFields on File {
    id
    path
    url
    mimeType
    filename
    size
    completed
    cancelled
    permalink
    fileLastModified
    originalFile {
      id
    }
    context {
      id
      parentObj {
        id
        canUpdate
      }
    }
    conversionGltfjson {
      id
      status
      reason
      errorCode
      convertedFile {
        id
      }
    }
    creator {
      id
      username
      profile {
        id
        avatar {
          id
          filename
          path
          url
          completed
        }
        imageFallbackChar
      }
    }
  }
`;

export const NEW_FILE_FIELDS = gql`
  fragment newFileFields on File {
    id
    path
    url
    filename
    completed
    cancelled
    permalink
    uploadUrl
    private
    isCopy
    size
  }
`;

export const FILE = gql`
  ${FILE_FIELDS}
  query File($id: ID) {
    file(id: $id) {
      ...fileFields
    }
  }
`;

export class FileQuery extends Query {
  static conf = {
    GQL: FILE,
    varNames: ['id'],
    resultPath: 'file',
  };
}

export const FILE_MUTATION = gql`
  ${NEW_FILE_FIELDS}
  mutation File($fileInput: FileInput) {
    file(fileData: $fileInput) {
      file {
        ...newFileFields
      }
    }
  }
`;

export class FileMutation extends Mutation {
  static conf = {
    GQL: FILE_MUTATION,
    inputName: 'fileInput',
    resultPath: 'file.file',
  };
}

export const BULK_FILE_MUTATION = gql`
  ${NEW_FILE_FIELDS}
  mutation BulkFiles($filesInput: [FileInput]) {
    files(filesData: $filesInput) {
      files {
        ...newFileFields
      }
    }
  }
`;

export class BulkFileMutation extends Mutation {
  static conf = {
    GQL: BULK_FILE_MUTATION,
    inputName: 'filesInput',
    resultPath: 'files.files',
  };
}

export const COMPLETE_FILE_MUTATION = gql`
  ${FILE_FIELDS}
  mutation CompleteFile($fileInput: FileInput) {
    file(fileData: $fileInput) {
      file {
        ...fileFields
      }
    }
  }
`;

export class CompleteFileMutation extends Mutation {
  static conf = {
    GQL: COMPLETE_FILE_MUTATION,
    inputName: 'fileInput',
    resultPath: 'file.file',
  };
}

const BULK_COMPLETE_FILE_MUTATION = gql`
  ${FILE_FIELDS}
  mutation BulkCompleteFile($filesInput: [FileInput]) {
    files(filesData: $filesInput) {
      files {
        ...fileFields
      }
    }
  }
`;

export class BulkCompleteFileMutation extends Mutation {
  static conf = {
    GQL: BULK_COMPLETE_FILE_MUTATION,
    inputName: 'filesInput',
    resultPath: 'files.files',
  };
}

const CHECKIN_STAGE_MUTATION = gql`
  mutation CheckinStage($checkinStageInput: CheckinStageInput) {
    checkinStage(checkinStageData: $checkinStageInput) {
      result
    }
  }
`;

export class CheckinStageMutation extends Mutation {
  static conf = {
    GQL: CHECKIN_STAGE_MUTATION,
    inputName: ['checkinStageInput', 'fileSync', 'stagingUuid'],
    resultPath: 'checkinStage.result',
  };
}

const RESET_STAGE_MUTATION = gql`
  mutation ResetStage($resetData: ResetContributionInput) {
    resetContribution(resetData: $resetData) {
      project {
        id
      }
    }
  }
`;

export class ResetStageMutation extends Mutation {
  static conf = {
    GQL: RESET_STAGE_MUTATION,
    inputName: 'resetData',
    resultPath: 'resetContribution.project',
  };
}

const CONVERSION_PROGRESS_FRAGMENT = gql`
  fragment conversionProgressStatus on FileProcess {
    fileId
    status
    reason
    errorCode
    progress
    steps
    type
  }
`;

const CONVERSION_PROGRESS_SUBSCRIPTION = gql`
  ${CONVERSION_PROGRESS_FRAGMENT}
  subscription ConversionProgressSubscription(
    $subscribeFileProcessInput: SubscribeFileProcessInput!
  ) {
    fileProcess(subscribeFileProcessInput: $subscribeFileProcessInput) {
      ...conversionProgressStatus
    }
  }
`;

export class ConversionProgressSubscription extends Subscription {
  static conf = {
    GQL: CONVERSION_PROGRESS_SUBSCRIPTION,
    resultPath: 'fileProcess',
  };
}

const CONVERSION_PROGRESS_POLLING = gql`
  query File($id: ID) {
    file(id: $id) {
      id
      conversionGltfjson {
        id
        status
        convertedFile {
          id
        }
      }
    }
  }
`;

export class FileConversionPolling extends Query {
  static conf = {
    GQL: CONVERSION_PROGRESS_POLLING,
    resultPath: 'file',
  };
}
